<template>
  <div
    :class="[
      $style['mds-range-slider'],
      $style['mds-range-sliderWithRange'],
      getClassObject(),
    ]"
    :disabled="disabled"
    style="position: relative"
  >
    <mds-input
      :class="$style['mds-range-slider__input']"
      type="text"
      autocomplete="off"
      :label="lowerLabel"
      :value="`${FormatedlowerValue}`"
      v-bind="$attrs"
      :disabled="disabled"
      :min="min"
      :max="max"
      :size="size"
      :step="step"
      :on-dark="onDark"
      @keypress="numbersOnly"
      @focus="changeFormatToNumber"
      @blur="triggerChange($event.target.value, upperValue, $event)"
      @input="subscribeInputChange(arguments)"
      :maxlength="maxlength"
    ></mds-input>

    <div :class="$style['mds-range-slider__wrapper']">
      <div
        :class="[
          $style['mds-range-slider__visual'],
          $style['mds-range-slider--horizontal'],
          onDragClass,
        ]"
        :aria-valuemin="min"
        :aria-valuemax="inputMax"
      >
        <div ref="track" :class="$style['mds-range-slider__base']">
          <div
            :class="$style['mds-range-slider__origin']"
            :style="leftHandleStyles"
            @touchstart="onLowerHandleMouseDown"
            @mousedown="onLowerHandleMouseDown"
          >
            <div
              :class="[
                $style['mds-range-slider__handle'],
                activeLowerKnobClass,
              ]"
              data-handle="0"
              :disabled="disabled"
              tabindex="-1"
              @keydown="onLowerHandleKeyDown"
            ></div>
          </div>
          <div
            :class="$style['mds-range-slider__connect']"
            :style="connectStyles"
          ></div>
          <div
            v-if="range"
            :class="$style['mds-range-slider__origin']"
            :style="rightHandleStyles"
            @touchstart="onUpperHandleMouseDown"
            @mousedown="onUpperHandleMouseDown"
          >
            <div
              :class="[
                $style['mds-range-slider__handle'],
                activeUpperKnobClass,
              ]"
              data-handle="0"
              tabindex="-1"
              @keydown="onUpperHandleKeyDown"
            />
          </div>
        </div>
      </div>
    </div>
    <mds-input
      v-if="range"
      :class="$style['mds-range-slider__input']"
      type="number"
      autocomplete="off"
      :label="upperLabel"
      :value="`${upperValue.toFixed(decimalPoints)}`"
      v-bind="$attrs"
      :disabled="disabled"
      :min="min"
      :max="max"
      :size="size"
      :step="step"
      :on-dark="onDark"
      @blur="triggerChange(lowerValue, $event.target.value, $event)"
      @input="subscribeInputChangeOnRange(arguments)"
    ></mds-input>

    <div v-if="isShowRefreshIcon" :class="$style['range-refreshWrap']">
      <a :class="$style['my-link']" @click="refreshValue" href="#">{{
        refreshText
      }}</a>

      <mds-button
        variation="icon-only"
        icon="info-circle"
        type="button"
        @mouseover="toggleRangeTT = true"
        @mouseleave="toggleRangeTT = false"
        @focus="toggle = true"
        @blur="toggleRangeTT = false"
        id="default-trigger-range"
        size="small"
      >
        User
      </mds-button>

      <mds-tooltip
        triggered-by="default-trigger-range"
        v-model="toggleRangeTT"
        :position="['right-center']"
      >
        {{ tooltipMessage }}
      </mds-tooltip>
    </div>
  </div>
</template>

<script>
import MdsInput from "@mds/input";
import { format } from "d3-format";
import { MdsButton } from "@mds/button";
import MdsTooltip from "@mds/tooltip";

export default {
  name: "AdlRangeSlider",
  inheritAttrs: false,
  model: {
    prop: "value",
    event: "change",
  },
  components: {
    MdsInput,
    MdsButton,
    MdsTooltip,
  },
  props: {
    maxlength: {
      type: Number,
      default: 9,
    },
    decimalPoints: {
      type: Number,
      default: 0,
      docs: {
        validation: "—",
        description:
          "Sets the number of digits after the decimal point to use.",
      },
    },
    disabled: {
      type: Boolean,
      default: false,
      docs: {
        validation: "—",
        description:
          "If `true`, changes component to a disabled state variation.",
      },
    },
    isShowRefreshIcon: {
      type: Boolean,
      default: false,
    },
    lowerLabel: {
      type: String,
      default: "",
    },
    lowerLabel: {
      type: String,
      docs: {
        validation: "—",
        description:
          "Sets the lower input label for a single selection variation.",
      },
    },
    max: {
      type: Number,
      default: 100,
      docs: {
        validation: "—",
        description: "Sets the maximum possible value for the range slider.",
      },
    },
    inputMax: {
      type: Number,
      default: 100,
      docs: {
        validation: "—",
        description: "Sets the maximum possible value for the range slider.",
      },
    },
    min: {
      type: Number,
      default: 0,
      docs: {
        validation: "—",
        description: "Sets the minimum possible value for the range slider.",
      },
    },
    tooltipMessage: {
      type: String,
    },
    refreshText: {
      type: String,
    },
    onDark: {
      type: Boolean,
      default: false,
      docs: {
        validation: "—",
        description: "If `true`, changes component to on-dark styling.",
      },
    },
    range: {
      type: Boolean,
      default: false,
      docs: {
        validation: "—",
        description:
          "If `true`, adds an additional handle and input to the range slider to allow it to represent a range of values.",
      },
    },
    size: {
      type: String,
      default: "medium",
      validator(value) {
        return ["small", "medium", "large", "touch"].indexOf(value) !== -1;
      },
      docs: {
        validation: "One of: `small`, `medium`, `large`, `touch`.",
        description: "Sets the size of the button.",
      },
    },
    step: {
      type: Number,
      default: 1,
      docs: {
        validation: "—",
        description: "Sets the number of steps between each handle move.",
      },
    },
    upperLabel: {
      type: String,
      docs: {
        validation: "—",
        description:
          "Sets the upper input label for a multiple selection variation.",
      },
    },
    inputFormat: {
      type: String,
      default: "",
    },
    isCurrencyFormat: {
      type: Boolean,
    },
    isShowDecimal: {
      type: Boolean,
    },
    value: {
      type: [Number, Object],
      default: 0,
      docs: {
        validation: "—",
        description:
          "Sets the current `value` for the range slider. If `range=true`, it should be passed as a range, e.g., `{ min: 25, max: 75 }`.",
        support: ["v-model"],
      },
    },
  },
  data() {
    return {
      lowerHandleActive: false,
      upperHandleActive: false,
      isFocus: false,
      refreshKey: 0,
      toggleRangeTT: false,
    };
  },
  computed: {
    classObject() {
      const obj = {};
      obj[this.$style["mds-component--on-dark"]] = this.onDark;
      return obj;
    },
    sizeClass() {
      return this.$style[`mds-range-slider--${this.size}`];
    },
    activeLowerKnobClass() {
      const obj = {};
      obj[this.$style["mds-range-slider__handle--active"]] =
        this.lowerHandleActive;
      return obj;
    },
    activeUpperKnobClass() {
      const obj = {};
      obj[this.$style["mds-range-slider__handle--active"]] =
        this.upperHandleActive;
      return obj;
    },
    onDragClass() {
      const obj = {};
      obj[this.$style["mds-range-slider--on-drag"]] = this.isDragging;
      return obj;
    },
    lowerValue() {
      const { value, range } = this;
      const { min: lower = value } = value;
      const maxValue = range ? value.max : this.max;

      return this.clamp(lower, this.min, maxValue);
    },
    FormatedlowerValue() {
      const { value, range } = this;
      this.refreshKey;
      const { min: lower = value } = value;
      const maxValue = range ? value.max : this.inputMax;
      let lowervalue = 0;
      lowervalue = this.clamp(lower, this.min, maxValue);
      if (!this.focus) {
        if (!this.isCurrencyFormat) {
          if (this.isShowDecimal) {
            this.$emit("change", Number(lowervalue.toFixed(2)));
            return lowervalue + this.inputFormat;
          } else {
            return lowervalue + this.inputFormat;
          }
        } else {
          return format(this.inputFormat)(lowervalue);
        }
      } else {
        if (lowervalue == 0) {
          return "";
        } else {
          return lowervalue;
        }
      }
    },
    upperValue() {
      const { value, range } = this;
      const { max: upper = value } = value;

      return !range ? this.max : this.clamp(upper, this.min, this.max);
    },
    percentageLowerValue() {
      return this.getPercentageValue(this.lowerValue, this.min, this.max);
    },
    percentageUpperValue() {
      return this.getPercentageValue(this.upperValue, this.min, this.max);
    },
    leftHandleStyles() {
      return { left: `${this.percentageLowerValue}%` };
    },
    rightHandleStyles() {
      return {
        right: `${100 - this.percentageUpperValue}%`,
        zIndex: 100 - this.percentageUpperValue > 50 ? 1 : 0, // prevents handles locking
      };
    },
    connectStyles() {
      return {
        left: `${this.range ? this.percentageLowerValue : 0}%`,
        right: `${
          this.range
            ? 100 - this.percentageUpperValue
            : 100 - this.percentageLowerValue
        }%`,
      };
    },
    isDragging() {
      return this.lowerHandleActive || this.upperHandleActive;
    },
  },
  beforeDestroy() {
    this.unsubscribeGlobalEvents();
  },

  methods: {
    getClassObject() {
      const obj = {};
      obj[this.$style["setlatestbalance"]] = this.isShowRefreshIcon;
      return obj;
    },
    getlengthwithoutdecimal(value) {
      let hasPercentage = this.inputFormat.indexOf("%") > -1;
      let hasdecimal = value.indexOf(".") > -1;
      if (hasPercentage) {
        if (hasdecimal) return this.maxlength;
        else return this.maxlength - 3;
      } else {
        return maxlength;
      }
    },

    refreshValue() {
      this.$emit("refresh");
    },
    changeFormatToNumber(evt) {
      this.focus = true;
      this.refreshKey++;
    },
    numbersOnly(evt) {
      evt = evt ? evt : window.event;
      var charCode = evt.which ? evt.which : evt.keyCode;
      if (charCode == 13) {
        this.$emit("change", Number(evt.currentTarget.value));
      }
      if (
        charCode > 31 &&
        (charCode < 48 || charCode > 57) &&
        charCode !== 46
      ) {
        evt.preventDefault();
      } else {
        if (charCode == 46) {
          if (evt.currentTarget.value.indexOf(".") < 0) {
            return true;
          } else {
            evt.preventDefault();
          }
        }
        return true;
      }
    },
    clamp(number, lower, upper) {
      return Math.max(lower, Math.min(number, upper));
    },
    approxToStep(value, min, step) {
      const mismatch = (value - min) % step;

      if (mismatch !== 0) {
        return value - mismatch;
      }
      return value;
    },
    adjustValues(value, min, max, step) {
      return this.approxToStep(this.clamp(value, min, max), min, step);
    },
    getPercentageValue(value, min, max) {
      return Math.round(((value - min) / (max - min)) * 100);
    },
    scaleRange(scale, min, max) {
      return min + scale * (max - min);
    },
    projectDistanceOntoRange(dist, offset, length, min, max) {
      return this.scaleRange((dist - offset) / length, min, max);
    },
    getKeyIncrement(key) {
      const DECREMENT_KEYS = [
        "Down", // IE/Edge specific value
        "ArrowDown",
        "Left", // IE/Edge specific value
        "ArrowLeft",
      ];

      const INCREMENT_KEYS = [
        "Up", // IE/Edge specific value
        "ArrowUp",
        "Right", // IE/Edge specific value
        "ArrowRight",
      ];
      if (DECREMENT_KEYS.includes(key)) {
        return -1;
      }
      if (INCREMENT_KEYS.includes(key)) {
        return 1;
      }
      return 0;
    },
    triggerChange(newLowerValue, newUpperValue, event) {
      this.focus = false;
      this.refreshKey++;
      const { range, lowerValue, upperValue } = this;

      if (range) {
        const validLowerValue =
          newLowerValue >= upperValue ? upperValue : newLowerValue;
        const validUpperValue =
          newUpperValue <= lowerValue ? lowerValue : newUpperValue;

        this.$emit("change", {
          min: validLowerValue,
          max: validUpperValue,
        });
      } else {
        this.$emit(
          "change",
          Number(newLowerValue) >= this.inputMax
            ? this.inputMax
            : Number(newLowerValue) <= this.min
            ? this.min
            : Number(newLowerValue)
        );
      }

      // Custom event if teams wish to listen
      this.$emit("mds-range-slider-change", event); // TODO: Update in next major change, event name should be past tense
    },
    subscribeInputChange(event) {
      if (isNaN(event[1].target.value)) {
        event[1].target.value = "0";
        return "0";
      }
      const type = event[1].inputType;

      let newValue = event[1].target.value;
      newValue =
        Number(newValue) >= this.inputMax
          ? this.inputMax
          : Number(newLowerValue) <= this.min
          ? this.min
          : Number(newLowerValue);
      event[1].target.value = newValue;
      event[0] = newValue;

      // if (newValue.length > this.maxlength) {
      //   newValue = newValue.substring(
      //     0,
      //     this.maxlength
      //   );
      //   event[1].target.value = newValue;
      //   event[0] = newValue;
      // }

      if (type === undefined) {
        this.triggerChange(event[0], this.upperValue, event);
      }
    },
    subscribeInputChangeOnRange(event) {
      const type = event[1].inputType;

      if (type === undefined) {
        this.triggerChange(this.lowerValue, event[0], event);
      }
    },
    onLowerHandleMouseDown() {
      if (!this.disabled) {
        this.lowerHandleActive = true;
        this.subscribeGlobalEvents();
      }
    },
    onUpperHandleMouseDown() {
      if (!this.disabled) {
        this.upperHandleActive = true;
        reset;
        this.subscribeGlobalEvents();
      }
    },
    subscribeGlobalEvents() {
      global.addEventListener("mouseup", this.reset);
      global.addEventListener("mousemove", this.onWindowMouseMove);
      global.addEventListener("touchend", this.reset);
      global.addEventListener("touchmove", this.onWindowMouseMove);
    },
    unsubscribeGlobalEvents() {
      global.removeEventListener("mouseup", this.reset);
      global.removeEventListener("mousemove", this.onWindowMouseMove);
      global.removeEventListener("touchend", this.reset);
      global.removeEventListener("touchmove", this.onWindowMouseMove);
    },
    reset() {
      this.lowerHandleActive = false;
      this.upperHandleActive = false;
      this.unsubscribeGlobalEvents();
    },
    onLowerHandleKeyDown(event) {
      const inc = this.getKeyIncrement(event.key);

      if (!this.disabled) {
        this.triggerChange(
          this.lowerValue + inc * this.step,
          this.upperValue,
          event
        );
      }
    },
    onUpperHandleKeyDown(event) {
      const inc = this.getKeyIncrement(event.key);

      if (!this.disabled) {
        this.triggerChange(
          this.lowerValue,
          this.upperValue + inc * this.step,
          event
        );
      }
    },
    onWindowMouseMove(event) {
      const {
        min,
        max,
        step,
        $refs,
        lowerValue,
        upperValue,
        isDragging,
        lowerHandleActive,
        upperHandleActive,
      } = this;

      if (!isDragging || !$refs.track) {
        return;
      }

      const { clientX } = event.touches ? event.touches[0] : event;
      const { left: trackX, width: trackWidth } =
        $refs.track.getBoundingClientRect();

      const newValue = this.projectDistanceOntoRange(
        clientX,
        trackX,
        trackWidth,
        min,
        max
      );

      if (lowerHandleActive) {
        this.triggerChange(
          this.adjustValues(newValue, min, upperValue, step),
          upperValue,
          event
        );
      } else if (upperHandleActive) {
        this.triggerChange(
          lowerValue,
          this.adjustValues(newValue, lowerValue, max, step),
          event
        );
      }
    },
  },
};
</script>

<style lang="scss" module>
@import "./range-slider.scss";

.mds-range-slider {
  .mds-range-slider__wrapper {
    padding-right: 8px;
  }
  &.setlatestbalance {
    margin-bottom: 32px !important;

    .range-refreshWrap {
      bottom: -32px !important;
    }
  }
}
</style>
